import { ANIMATION_LABEL } from '@/components/admin/experiences/page-editor/contracts';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent, withNoyauTextsFollowingBlocks } from '@/lib/experience-draft';
import type {
  AnimationSpeed,
  BlockType,
  DisplayCondition,
  ExperienceBlockDraft,
  ExperiencePageDraft,
  TempsDraft,
} from '@matter_extractor/shared';
import {
  ANIMATION_SPEED_MS,
  EMPTY_BLOCK_VISUAL,
  EMPTY_RICH_TEXT,
  EXPERIENCE_BLOCKS_MAX,
  EXPERIENCE_SELECTION_OPTIONS_MAX,
  EXPERIENCE_SELECTION_OPTIONS_MIN,
  EXPERIENCE_TEMPS_MAX,
  FIRST_TEMPS_START,
  NOYAU_MAX_GAP_DEFAULT,
  namedVariablesOf,
} from '@matter_extractor/shared';

/**
 * Ce que l'éditeur de page FAIT à une page, en fonctions pures (refonte
 * modulaire §9.2, §9.3, H23, H26, H27, H30; issue #58).
 *
 * The stack, the « Ajouter » grid, the two inspectors, the tracks and the
 * keyboard reorder all ask for the same handful of edits, from five different
 * components. Written inside any one of them, the second caller would have
 * copied it and the two copies would have disagreed about what a vidé temps
 * does - which is exactly the drift `protocol-draft.ts` names at its own head.
 * So they live here, pure, and `vitest.config.ts` measures them without a DOM.
 *
 * ## `withPage`, and why this module never touches `experience-draft.ts`
 *
 * The editor of a PAGE edits one page of the EXPERIENCE's draft and saves
 * through the same kit: there is one draft, one autosave, one version. So every
 * function below takes and returns a `ExperiencePageDraft`, and `withPage` is
 * the one line that puts a rewritten page back into the experience. The page
 * operations therefore never need to know that an experience exists, and the
 * timeline's own edits (`addPage`, `withPersonaRef`) stay where they are.
 *
 * ## Un temps vidé disparaît, et le premier temps part avec la page
 *
 * H27, and both are maintained on EVERY gesture rather than checked at the
 * save. `experienceContentSchema` refuses a first temps that does not start
 * with the page, so a `removeBlock` that emptied temps 1 and left temps 2 -
 * which starts after a delay, or at a click - in first position would make the
 * draft unsaveable without anything on screen saying why. `seatFirst` runs
 * after every gesture that can change which temps comes first.
 *
 * And VIDÉ is read strictly: `pruneEmptied` compares the page before and after,
 * so it drops a temps this gesture emptied and never one that was born empty.
 * « Ajouter un temps » creates an empty track on purpose; a prune that judged
 * emptiness alone would delete it on the very next gesture.
 *
 * ## Les identifiants sont des uuid, uniques dans le contenu
 *
 * `experienceContentSchema` refuses a duplicate page, temps or block id inside
 * one experience (§8.6: a `{{champ:<blockId>}}` addresses a block, a
 * `{{choix:<pageId>}}` a page). `crypto.randomUUID` is what the protocol's
 * editor already mints step ids with, and it is what every `new*` below uses -
 * never a counter, which would collide with whatever the template already
 * carries and, worse, would collide with itself across two pages.
 */

/** A fresh local id, unique inside one content by construction (§8.6). */
export function newLocalId(): string {
  return crypto.randomUUID();
}

/* ------------------------------------------------------- crossing the draft */

/**
 * The experience's draft with ONE page rewritten - the only line of this module
 * that knows an experience exists.
 *
 * A no-op when the id names no page, and that is not defensiveness: the route
 * is an address somebody can paste, so `?pageId=` naming a page a colleague
 * deleted has to leave the draft alone rather than append a page nobody asked
 * for.
 *
 * The one experience-level consequence of a page edit is spelled here too: the
 * reference to the texts of the noyaux follows the Noyau blocks (noyau.md §3.5,
 * N13) - the first one posed brings it, the last one removed takes it away.
 */
export function withPage(
  draft: ExperienceDraft,
  pageId: string,
  updater: (page: ExperiencePageDraft) => ExperiencePageDraft,
): ExperienceDraft {
  if (!draft.pages.some((page) => page.id === pageId)) return draft;
  return withNoyauTextsFollowingBlocks({
    ...draft,
    pages: draft.pages.map((page) => (page.id === pageId ? updater(page) : page)),
  });
}

/** The page the editor is on, or null when the address names none. */
export function pageOf(draft: ExperienceDraft, pageId: string): ExperiencePageDraft | null {
  return draft.pages.find((page) => page.id === pageId) ?? null;
}

/** Where a page sits in the line, or -1. */
export function pageIndexOf(draft: ExperienceDraft, pageId: string): number {
  return draft.pages.findIndex((page) => page.id === pageId);
}

/**
 * What the navigation banner says: the two neighbours, the rank and the total
 * (§9.3, maquette `EditeurPageEcran`).
 *
 * `previous` is null on the first page, where the banner reads « Début de
 * l'expérience » and the control is inactive; `next` is null on the last, where
 * the transition of the page is never played.
 */
export type PageNeighbours = {
  readonly index: number;
  readonly total: number;
  readonly previous: ExperiencePageDraft | null;
  readonly next: ExperiencePageDraft | null;
};

export function pageNeighbours(draft: ExperienceDraft, pageId: string): PageNeighbours {
  const index = pageIndexOf(draft, pageId);
  return {
    index,
    total: draft.pages.length,
    previous: index > 0 ? (draft.pages[index - 1] ?? null) : null,
    next: index >= 0 ? (draft.pages[index + 1] ?? null) : null,
  };
}

/* ------------------------------------------------------------- fresh things */

/** A temps nobody has filled yet: normal speed, and a delay of half a second. */
export function newTemps(): TempsDraft {
  return {
    id: newLocalId(),
    speed: 'normal',
    /*
      Half a second and not zero: a temps added at the end exists to make
      something appear LATER, and a delay of 0 would make the gesture look like
      it did nothing. The first temps overrides it through `seatFirst` anyway.
    */
    start: { kind: 'delay', seconds: 0.5 },
    blocks: [],
  };
}

/**
 * A block of one type, with the defaults §9.2 gives it.
 *
 * The three labelled types are born EMPTY, which is the draft régime of §4 met
 * in the block: `experienceBlockDraftSchema` allows a blank label where
 * `experienceBlockSchema` does not, so a button somebody has just added is
 * legal in the draft and refused as a version - and the save box says so.
 *
 * A Son is born with `animation: 'none'`, which is the one place this default
 * differs from the schema's `fade`: §9.3 says « un son n'en a pas », and a
 * sound carrying `fade` in the store is a row the reader ignores but the
 * inspector would have to explain.
 *
 * Every block is born « toujours affiché » - no display condition (§9.2) - and
 * an Input without « Nom de la personne dans Sessions », both the defaults of
 * the schema. A Noyau block is born a first choice, the draft's default and the
 * block every sequence starts with (noyau.md §3.2, N10), with the default gap
 * threshold of its first choice, 5 points (§3.8, issue #169).
 */
export function newBlock(type: BlockType): ExperienceBlockDraft {
  const common = {
    id: newLocalId(),
    animation: 'fade',
    visual: EMPTY_BLOCK_VISUAL,
    conditions: [] as DisplayCondition[],
  } as const;
  switch (type) {
    case 'text':
      return { ...common, type: 'text', text: EMPTY_RICH_TEXT };
    case 'image':
      return { ...common, type: 'image', assetId: null, caption: '' };
    case 'sound':
      return { ...common, animation: 'none', type: 'sound', assetId: null, loop: false, volume: 1 };
    case 'button':
      return {
        ...common,
        type: 'button',
        label: '',
        action: 'next-page',
        // Empty, the schema's default: the reader then says « Un instant... » (#174).
        waitingLabel: '',
        rememberChoice: false,
        variableName: '',
      };
    case 'input':
      return {
        ...common,
        type: 'input',
        label: '',
        lines: 'single',
        required: false,
        format: 'text',
        variableName: '',
        personName: false,
      };
    case 'checkbox':
      return { ...common, type: 'checkbox', label: '', required: false, variableName: '' };
    case 'link':
      return { ...common, type: 'link', label: '', url: '' };
    case 'copyable':
      return { ...common, type: 'copyable', text: '' };
    case 'noyau':
      return { ...common, type: 'noyau', mode: 'first-choice', maxGap: NOYAU_MAX_GAP_DEFAULT };
    // Born as the schema reads them (retour-experience.md §4): a Sélection holds
    // its two EMPTY options - the minimum, to be written, refused at the save
    // while they are blank (`selection-options`) - and a Note has no scale to set.
    case 'selection':
      return {
        ...common,
        type: 'selection',
        question: '',
        options: ['', ''],
        required: false,
        variableName: '',
      };
    case 'note':
      return { ...common, type: 'note', question: '', required: false, variableName: '' };
  }
}

/* -------------------------------------------------------------- the reading */

/** One block of the stack, with everything a row needs to draw itself. */
export type StackEntry = {
  readonly block: ExperienceBlockDraft;
  readonly tempsId: string;
  readonly tempsIndex: number;
  /** Its rank inside its temps - what a drop target and the keyboard move use. */
  readonly index: number;
  /** Its rank in the whole page, which is the order the reader plays. */
  readonly rank: number;
};

/** Every block of the page in reading order, each knowing where it sits. */
export function stackEntries(page: ExperiencePageDraft): StackEntry[] {
  const entries: StackEntry[] = [];
  page.temps.forEach((temps, tempsIndex) => {
    temps.blocks.forEach((block, index) => {
      entries.push({ block, tempsId: temps.id, tempsIndex, index, rank: entries.length });
    });
  });
  return entries;
}

/** Where one block sits, or null when the page does not hold it. */
export function entryOf(page: ExperiencePageDraft, blockId: string): StackEntry | null {
  return stackEntries(page).find((entry) => entry.block.id === blockId) ?? null;
}

/** How many blocks a page holds, across every temps. */
export function pageBlockCount(page: ExperiencePageDraft): number {
  return page.temps.reduce((total, temps) => total + temps.blocks.length, 0);
}

/* ---------------------------------------------------------- the invariants */

/**
 * Seat the first temps on the page: a delay of 0, which does not adjust (H27).
 *
 * `experienceContentSchema` refuses anything else on `temps[0]`, so this runs
 * after EVERY gesture that can change which temps comes first - otherwise a
 * `removeTemps` on temps 1 would leave a page whose save is refused with
 * nothing on screen saying why.
 */
function seatFirst(page: ExperiencePageDraft): ExperiencePageDraft {
  const first = page.temps[0];
  if (first === undefined) return page;
  if (first.start.kind === 'delay' && first.start.seconds === 0) return page;
  return { ...page, temps: [{ ...first, start: FIRST_TEMPS_START }, ...page.temps.slice(1)] };
}

/**
 * « Un temps VIDÉ de ses blocs disparaît » (H27) - and the emphasis is the
 * whole of this function.
 *
 * It drops a temps that is empty NOW and was NOT empty before, never one that
 * was born empty: « Ajouter un temps » creates an empty track on purpose, and a
 * prune that judged emptiness alone would delete it on the operator's very next
 * gesture - a button that appears to do nothing, which is the worst way for a
 * feature to be broken.
 *
 * A page always keeps at least one temps: the stack needs somewhere to drop the
 * next block, and `newContentPage` is born with exactly that.
 */
function pruneEmptied(
  before: ExperiencePageDraft,
  after: ExperiencePageDraft,
): ExperiencePageDraft {
  const wasFilled = new Set(
    before.temps.filter((temps) => temps.blocks.length > 0).map((temps) => temps.id),
  );
  const kept = after.temps.filter((temps) => temps.blocks.length > 0 || !wasFilled.has(temps.id));
  const temps = kept.length === 0 ? after.temps.slice(0, 1) : kept;
  return seatFirst({ ...after, temps });
}

/* ---------------------------------------------------------------- the edits */

/**
 * « Ajouter » - one block, joining the temps of the SELECTED block, or the last
 * one (§9.3).
 *
 * The selection and not the end of the page: somebody who has just clicked a
 * block is working there, and a block that landed three temps further down is a
 * block they then have to drag back. With nothing selected the last temps is
 * the honest answer - it is where the page is still being written.
 *
 * Refused above `EXPERIENCE_BLOCKS_MAX` per temps, which is where the schema
 * puts the ceiling: a twenty-first block that vanished at the save would take
 * whatever was typed into it with it.
 */
export function addBlock(
  page: ExperiencePageDraft,
  type: BlockType,
  selectedBlockId: string | null,
): { readonly page: ExperiencePageDraft; readonly blockId: string | null } {
  /*
    A page with NO temps is a page a stored version can hold - the protocol page
    of the template is born `temps: []` - so the first block seeds one rather
    than being refused. It carries `FIRST_TEMPS_START`, which is the only start
    the schema accepts in first position.
  */
  const seeded =
    page.temps.length === 0
      ? { ...page, temps: [{ ...newTemps(), start: FIRST_TEMPS_START }] }
      : page;
  const selected = selectedBlockId === null ? null : entryOf(seeded, selectedBlockId);
  const tempsIndex = selected?.tempsIndex ?? seeded.temps.length - 1;
  const temps = seeded.temps[tempsIndex];
  if (temps === undefined || temps.blocks.length >= EXPERIENCE_BLOCKS_MAX) {
    return { page, blockId: null };
  }
  const block = newBlock(type);
  const next = seeded.temps.map((one, index) =>
    index === tempsIndex ? { ...one, blocks: [...one.blocks, block] } : one,
  );
  return { page: { ...seeded, temps: next }, blockId: block.id };
}

/** « Ajouter un temps » - after the last one, empty, and it stays (§9.3). */
export function addTemps(page: ExperiencePageDraft): ExperiencePageDraft {
  if (page.temps.length >= EXPERIENCE_TEMPS_MAX) return page;
  return { ...page, temps: [...page.temps, newTemps()] };
}

/** One block rewritten in place - what every inspector field calls. */
export function replaceBlock(
  page: ExperiencePageDraft,
  block: ExperienceBlockDraft,
): ExperiencePageDraft {
  return {
    ...page,
    temps: page.temps.map((temps) => ({
      ...temps,
      blocks: temps.blocks.map((one) => (one.id === block.id ? block : one)),
    })),
  };
}

/** « Supprimer ce bloc » - and the temps it emptied goes with it (H27). */
export function removeBlock(page: ExperiencePageDraft, blockId: string): ExperiencePageDraft {
  const stripped = {
    ...page,
    temps: page.temps.map((temps) => ({
      ...temps,
      blocks: temps.blocks.filter((block) => block.id !== blockId),
    })),
  };
  return pruneEmptied(page, stripped);
}

/**
 * Move a block to a given rank inside a given temps - the drop of §9.3.
 *
 * `index` is a rank in the temps AS IT WILL BE, the dragged block removed: that
 * is the one spelling a caller can compute from what it draws, and it is the
 * same arbitration `moveObjective` states in the protocol's list. A rank past
 * the end lands at the end rather than being refused, because a drop below the
 * last row is a gesture and not a mistake.
 */
export function moveBlock(
  page: ExperiencePageDraft,
  blockId: string,
  target: { readonly tempsId: string; readonly index: number },
): ExperiencePageDraft {
  const entry = entryOf(page, blockId);
  if (entry === null) return page;
  const destination = page.temps.find((temps) => temps.id === target.tempsId);
  if (destination === undefined) return page;
  if (destination.id !== entry.tempsId && destination.blocks.length >= EXPERIENCE_BLOCKS_MAX) {
    return page;
  }

  const without = page.temps.map((temps) => ({
    ...temps,
    blocks: temps.blocks.filter((block) => block.id !== blockId),
  }));
  const placed = without.map((temps) => {
    if (temps.id !== target.tempsId) return temps;
    const at = Math.max(0, Math.min(target.index, temps.blocks.length));
    return {
      ...temps,
      blocks: [...temps.blocks.slice(0, at), entry.block, ...temps.blocks.slice(at)],
    };
  });
  return pruneEmptied(page, { ...page, temps: placed });
}

/**
 * The KEYBOARD reorder: one step up or down in the page's reading order,
 * crossing the temps boundaries (§9.3).
 *
 * §9.3 asks that « un réordonnancement soit faisable sans souris », and the
 * frise of the protocol answers the same question with a native drag plus
 * explicit keys rather than with a library. One step in the FLAT order is what
 * makes the two gestures agree: the eye reads the stack as one column of
 * blocks, so an arrow that skipped a temps boundary would move the block
 * further than the row it is looking at.
 *
 * At a boundary the block joins the neighbouring temps at its near end, which
 * is the second half of the same gesture: pressing down on the last block of
 * temps 1 puts it first in temps 2, exactly where a drag onto the separator
 * would have put it.
 */
export function moveBlockBy(
  page: ExperiencePageDraft,
  blockId: string,
  delta: -1 | 1,
): ExperiencePageDraft {
  const entry = entryOf(page, blockId);
  if (entry === null) return page;
  const temps = page.temps[entry.tempsIndex];
  if (temps === undefined) return page;

  const inside = entry.index + delta;
  if (inside >= 0 && inside < temps.blocks.length) {
    return moveBlock(page, blockId, { tempsId: temps.id, index: inside });
  }

  const neighbour = page.temps[entry.tempsIndex + delta];
  if (neighbour === undefined) return page;
  return moveBlock(page, blockId, {
    tempsId: neighbour.id,
    index: delta === 1 ? 0 : neighbour.blocks.length,
  });
}

/**
 * « Apparaît au temps » - the SAFE gesture beside the drag (§9.3).
 *
 * The block joins the END of the chosen temps, which is where a reader meets it
 * last: the selector answers « when », the drag answers « when and where », and
 * confusing the two would make a selector silently reorder a temps.
 */
export function setBlockTemps(
  page: ExperiencePageDraft,
  blockId: string,
  tempsId: string,
): ExperiencePageDraft {
  const destination = page.temps.find((temps) => temps.id === tempsId);
  if (destination === undefined) return page;
  return moveBlock(page, blockId, { tempsId, index: destination.blocks.length });
}

/** One temps rewritten in place - its speed, its start. */
export function replaceTemps(page: ExperiencePageDraft, temps: TempsDraft): ExperiencePageDraft {
  return {
    ...page,
    temps: page.temps.map((one) => (one.id === temps.id ? temps : one)),
  };
}

/** « Ajouter un temps après » - from a temps' own inspector (§9.3). */
export function addTempsAfter(page: ExperiencePageDraft, tempsId: string): ExperiencePageDraft {
  if (page.temps.length >= EXPERIENCE_TEMPS_MAX) return page;
  const at = page.temps.findIndex((temps) => temps.id === tempsId);
  if (at === -1) return page;
  const next = [...page.temps];
  next.splice(at + 1, 0, newTemps());
  return { ...page, temps: next };
}

/**
 * « Supprimer ce temps » - « ses blocs rejoignent le précédent » (§9.3).
 *
 * Two cases the sentence does not cover, and both are decided here rather than
 * left to a caller: the FIRST temps has no previous one, so its blocks join the
 * NEXT and that one is re-seated on the page by `pruneTemps`; and the only
 * temps of a page is not removable at all, because the stack would then have
 * nowhere to drop a block and the page would need one invented back.
 */
export function removeTemps(page: ExperiencePageDraft, tempsId: string): ExperiencePageDraft {
  if (page.temps.length <= 1) return page;
  const at = page.temps.findIndex((temps) => temps.id === tempsId);
  if (at === -1) return page;
  const doomed = page.temps[at];
  if (doomed === undefined) return page;
  const host = at === 0 ? at + 1 : at - 1;
  const merged = page.temps.map((temps, index) => {
    if (index !== host) return temps;
    return {
      ...temps,
      blocks: at === 0 ? [...doomed.blocks, ...temps.blocks] : [...temps.blocks, ...doomed.blocks],
    };
  });
  const next = merged.filter((_, index) => index !== at);
  return seatFirst({ ...page, temps: next });
}

/* ------------------------------------------------------------- what it says */

/** « Temps 2 · normal · délai 1,5 s » - the résumé of a separator (§9.3). */
export const SPEED_LABEL: Readonly<Record<AnimationSpeed, string>> = {
  fast: 'rapide',
  normal: 'normal',
  slow: 'lent',
};

/** « 0,0 s » - a delay written the French way, one decimal, like the maquette. */
export function delayLabel(seconds: number): string {
  return `${seconds.toFixed(1).replace('.', ',')} s`;
}

export function tempsSummary(temps: TempsDraft, index: number): string {
  const start =
    temps.start.kind === 'click'
      ? 'au clic d’un bouton'
      : `délai ${delayLabel(temps.start.seconds)}`;
  return `Temps ${index + 1} · ${SPEED_LABEL[temps.speed]} · ${start}`;
}

/** Ce qu’un Son dit à la place d’une animation, une phrase entière (§9.3). */
export const SOUND_ANIMATION_SUMMARY =
  'Un son n’a pas d’animation d’entrée, il part au premier toucher';

/**
 * « Fondu · rapide » - ce que l’icône d’animation d’une ligne NE dit pas
 * (§9.3; issue #139).
 *
 * L’icône seule est un hiéroglyphe : rien ne dit que ce carré barré veut dire
 * « aucune » ni que cette flèche veut dire « glissé depuis la gauche ». La
 * bulle le dit, et elle y ajoute la rapidité, qui n’est écrite nulle part sur
 * la ligne - un bloc ne porte pas la sienne, il hérite de celle de son temps
 * (H27), et le séparateur qui la nomme peut être à cinq lignes au-dessus.
 *
 * Ici et pas dans le composant, pour la raison que porte l’en-tête de ce
 * module : la pile la dessine aujourd’hui, et la seconde surface qui la
 * voudrait la recopierait. Elle est la sœur de `tempsSummary` juste au-dessus,
 * et elle se mesure comme elle, sans DOM.
 *
 * Deux cas ne composent PAS les deux mots. Un Son n’a pas d’animation
 * d’entrée du tout, donc il porte sa propre phrase ; et « Aucune » n’a pas de
 * rapidité à hériter - « Aucune · rapide » se lirait comme une contradiction,
 * et la rapidité qu’il nommerait ne joue sur rien.
 */
export function blockAnimationSummary(block: ExperienceBlockDraft, temps: TempsDraft): string {
  if (block.type === 'sound') return SOUND_ANIMATION_SUMMARY;
  const named = ANIMATION_LABEL[block.animation];
  return block.animation === 'none' ? named : `${named} · ${SPEED_LABEL[temps.speed]}`;
}

/**
 * When each temps appears, counted from the start of the page, in milliseconds
 * (§9.3, H26, H27).
 *
 * « Le délai d'un temps se compte depuis la fin des animations du temps
 * précédent », so each step is the previous temps' animation plus this one's
 * delay - the very arithmetic `reader-play.ts` plays, restated here on the
 * WHOLE page because the editor asks a different question: not « when does the
 * next one come » but « how long is this page ». A temps waiting for a click
 * stops the count, which is why the answer is nullable from there on.
 */
export function tempsTimeline(page: ExperiencePageDraft): (number | null)[] {
  const times: (number | null)[] = [];
  let at: number | null = 0;
  page.temps.forEach((temps, index) => {
    if (index === 0) {
      times.push(0);
      return;
    }
    const previous = page.temps[index - 1];
    if (at === null || previous === undefined || temps.start.kind === 'click') {
      at = null;
      times.push(null);
      return;
    }
    const animated = previous.blocks.some(
      (block) => block.type !== 'sound' && block.animation !== 'none',
    );
    at = at + (animated ? ANIMATION_SPEED_MS[previous.speed] : 0) + temps.start.seconds * 1000;
    times.push(at);
  });
  return times;
}

/**
 * « Une durée automatique plus courte que le dernier délai est signalée, jamais
 * refusée » (H27, §9.3).
 *
 * Null when there is nothing to say: no automatic duration, or a duration that
 * outlives every temps. Otherwise the index of the FIRST temps the page would
 * cut off, which is what the inspector names - « avec une durée plus courte que
 * ce délai, ce temps n'apparaîtrait jamais ».
 */
export function cutOffTempsIndex(page: ExperiencePageDraft): number | null {
  if (page.autoDurationSeconds === null) return null;
  const budget = page.autoDurationSeconds * 1000;
  const times = tempsTimeline(page);
  const at = times.findIndex((time) => time !== null && time > budget);
  return at === -1 ? null : at;
}

/* -------------------------------------------------------------- the variables */

/**
 * The variable names the timeline declares MORE THAN ONCE (§8.6, §9.1).
 *
 * The server refuses `duplicate-variable-name` at the save and the dry run of
 * the box says so; this is what lets the inspector say it AT THE KEYSTROKE,
 * beside the field somebody is typing into. Two answers to one question, and
 * they agree because both count the same thing: `namedVariablesOf`, from
 * `shared`, which is also what the server walks.
 *
 * It takes the DRAFT and not a content, because a draft is what the editor
 * holds - so the shape is narrowed here to what `namedVariablesOf` reads rather
 * than pushed through `kit.normalized`, which answers null for a page nobody
 * has named yet and would leave the hint silent exactly while somebody types.
 */
export function duplicateVariableNames(draft: ExperienceDraft): Set<string> {
  const seen = new Map<string, number>();
  const named = namedVariablesOf(draftAsContent(draft));
  for (const entry of named) seen.set(entry.name, (seen.get(entry.name) ?? 0) + 1);
  return new Set([...seen.entries()].filter(([, count]) => count > 1).map(([name]) => name));
}

/** Does this page hold more than one Bouton? « Retenir le choix » needs two (§8.6). */
export function hasSeveralButtons(page: ExperiencePageDraft): boolean {
  return stackEntries(page).filter((entry) => entry.block.type === 'button').length > 1;
}

/* ------------------------------------------------- la pile, gestes de #136 */

/**
 * Un bloc neuf à la fin d'UN TEMPS NOMMÉ - le « + » de sa gouttière (§9.3,
 * grill du 2026-09-23).
 *
 * `addBlock` vise « le temps du bloc sélectionné, sinon le dernier », ce qui
 * oblige à sélectionner avant d'ajouter. Le « + » d'un séparateur, lui, NOMME
 * son temps : c'est tout ce qui les sépare, et c'est pourquoi les deux cohabitent
 * au lieu que l'un appelle l'autre avec un faux bloc sélectionné.
 *
 * À la fin de ce temps et non en tête : « ajouter dans ce temps » range à la
 * suite, et le glisser reste là pour le rang exact. Au plafond, la page revient
 * inchangée et l'identifiant est nul - le refus que `AddBlockGrid` rend déjà.
 */
export function addBlockToTemps(
  page: ExperiencePageDraft,
  type: BlockType,
  tempsId: string,
): { readonly page: ExperiencePageDraft; readonly blockId: string | null } {
  const at = page.temps.findIndex((one) => one.id === tempsId);
  const temps = page.temps[at];
  if (temps === undefined || temps.blocks.length >= EXPERIENCE_BLOCKS_MAX) {
    return { page, blockId: null };
  }
  const block = newBlock(type);
  const next = page.temps.map((one, index) =>
    index === at ? { ...one, blocks: [...one.blocks, block] } : one,
  );
  return { page: { ...page, temps: next }, blockId: block.id };
}

/**
 * Le bloc qui prend la sélection quand celui-ci s'en va (§9.3, grill du
 * 2026-09-23).
 *
 * Le SUIVANT du même temps, sinon le précédent, sinon rien - et c'est ce qui
 * rend « maintenir Suppr pour vider un temps » possible : la sélection descend
 * d'elle-même au lieu de retomber sur la page à chaque frappe.
 *
 * Dans le même temps et pas dans l'ordre de lecture entier : un `Suppr` qui
 * saute d'un temps à l'autre continuerait de vider la page bien après que
 * l'opérateur ait cessé de regarder ce qu'il efface.
 */
export function nextSelectedBlockId(
  page: ExperiencePageDraft,
  removedBlockId: string,
): string | null {
  const entry = entryOf(page, removedBlockId);
  if (entry === null) return null;
  const siblings = page.temps[entry.tempsIndex]?.blocks ?? [];
  return siblings[entry.index + 1]?.id ?? siblings[entry.index - 1]?.id ?? null;
}

/* -------------------------------------- les options d'une Sélection (#176) */

/**
 * « Ajouter une option » : one EMPTY option at the end of the list, or the list
 * unchanged at five (retour-experience.md §4.1, §7.2).
 *
 * An empty option and not a placeholder text: the field is then ready to be
 * typed in, and the save refuses an option that stays blank
 * (`selection-options`), so nothing invented can reach a version.
 */
export function addSelectionOption(options: readonly string[]): string[] {
  if (options.length >= EXPERIENCE_SELECTION_OPTIONS_MAX) return [...options];
  return [...options, ''];
}

/**
 * La croix d'une option : the list without it, or unchanged at two - « croix en
 * gouttière jusqu'à deux » (§7.2). A Sélection keeps at least two options, and a
 * list that went below would be a draft the save refuses with nothing wrong on
 * screen but a missing choice.
 */
export function removeSelectionOption(options: readonly string[], index: number): string[] {
  if (options.length <= EXPERIENCE_SELECTION_OPTIONS_MIN) return [...options];
  if (index < 0 || index >= options.length) return [...options];
  return options.filter((_, at) => at !== index);
}

/**
 * Réordonner une option : `from` goes to rank `to` in the list as it will READ
 * afterwards (the rank of the final array, not of the one the drag started
 * from). Out of range, or the same rank, leaves the list as it is.
 */
export function moveSelectionOption(
  options: readonly string[],
  from: number,
  to: number,
): string[] {
  if (from < 0 || from >= options.length) return [...options];
  const target = Math.min(Math.max(to, 0), options.length - 1);
  if (target === from) return [...options];
  const next = [...options];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...options];
  next.splice(target, 0, moved);
  return next;
}

/**
 * The options that repeat: the FIRST label written twice or more, with every
 * rank that holds it - what the red of the inspector marks (§7.2).
 *
 * Judged as `feedbackProblems` of `shared` judges it: on the TRIMMED label, an
 * empty one never counting - an empty option is its own refusal - and the case
 * kept, since the label written is the value kept and nothing folds it.
 */
export function repeatedSelectionOption(
  options: readonly string[],
): { readonly label: string; readonly ranks: readonly number[] } | null {
  const seen = new Map<string, number[]>();
  options.forEach((option, rank) => {
    const label = option.trim();
    if (label === '') return;
    seen.set(label, [...(seen.get(label) ?? []), rank]);
  });
  for (const [label, ranks] of seen) {
    if (ranks.length > 1) return { label, ranks };
  }
  return null;
}
